import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { getInstanceAuth } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

/** Per-instance auth method + connection details (CT-4194). */
export function useInstanceAuth(providerSlug, instanceId) {
  const { t } = useTranslation('admin-studio');
  const [auth, setAuth] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await getInstanceAuth(providerSlug, instanceId);
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setAuth(validateApiResponse(res, t('common.requestFailed')).body);
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? t('common.requestFailed'));
      setAuth(null);
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [providerSlug, instanceId, t]);

  useEffect(() => {
    load();
  }, [load]);

  return useMemo(() => ({ auth, isLoading, error }), [auth, isLoading, error]);
}
